import { supabase } from "@/integrations/supabase/client";
import { DbGoal, Goal, GoalCreationInput, mapDbGoalToUiGoal, mapUiGoalToDbGoal, mapGoalCreationInputToGoal } from "./goal-types";
import { ThreadsService } from "@/hooks/ai-agents/services/threads/threads.service";

export async function fetchGoals(userId: string): Promise<Goal[]> {
  // Note: RLS policies handle access control (owned + collaborative access)
  const { data, error } = await supabase
    .from("planner_goals")
    .select(`
      *,
      planner_goals_workbooks!inner (
        workbook:workbook_id (
          id,
          title,
          metadata
        )
      )
    `)
    .order("goal_order", { ascending: true });
  
  if (error) throw error;
  
  return (data as any[]).map(item => {
    const goal = mapDbGoalToUiGoal(item as DbGoal);
    // Add project title and cover image if available
    if (item.planner_goals_workbooks && item.planner_goals_workbooks.length > 0) {
      const workbook = item.planner_goals_workbooks[0].workbook;
      goal.projectTitle = workbook?.title;
      goal.projectId = workbook?.id;
      
      // Add cover image from project metadata if it exists
      if (workbook?.metadata && workbook.metadata.coverImage) {
        goal.projectCoverImage = workbook.metadata.coverImage as string;
      }
    }
    return goal;
  });
}

export async function createGoal(goalInput: GoalCreationInput, userId: string): Promise<Goal> {
  // Step 1: Create OpenAI thread first
  const { data: threadData, error: threadError } = await supabase.functions.invoke("threaded-text-generation", {
    body: { type: "create-thread" }
  });

  if (threadError || !threadData?.thread_id) {
    console.error('Failed to create OpenAI thread for goal:', threadError);
    throw new Error('Failed to create OpenAI thread for goal');
  }

  const openaiThreadId = threadData.thread_id;
  console.log('OpenAI thread created for new goal:', openaiThreadId);

  // Step 2: Convert input to proper Goal object with defaults
  const goal = mapGoalCreationInputToGoal(goalInput);
  
  // Step 3: Add the thread ID to the goal before mapping to database format
  const goalWithThread = { ...goal, openaiThreadId };
  
  // Step 4: Map to database format
  const toInsert = { ...mapUiGoalToDbGoal({ ...goalWithThread, id: crypto.randomUUID() }, userId) };
  delete toInsert.id;  // Let db handle id

  // Step 5: Insert goal with thread ID
  const { data, error } = await supabase
    .from("planner_goals")
    .insert([toInsert])
    .select()
    .single();
    
  if (error) {
    console.error('Failed to create goal in database:', error);
    throw error;
  }
  
  const createdGoal = mapDbGoalToUiGoal(data as DbGoal);
  console.log('Goal created successfully with thread ID:', openaiThreadId);

  // Step 6: Create agent thread with SAME openai_thread_id for unified conversation
  console.log('Creating agent thread for unified conversation...');
  try {
    const agentThread = await ThreadsService.createThread({
      userId,
      title: `Chat: ${goalInput.title}`, // Will be updated with project title later
      openaiThreadId, // Same OpenAI thread = unified conversation
      lastMsgFromUser: '',
      isPinned: false,
      isArchived: false,
      metadata: {
        projectContext: true,
        goalId: createdGoal.id,
        createdAt: new Date().toISOString()
      }
    });
    
    console.log('Agent thread created successfully:', agentThread.id);
    
    // Step 7: Return goal with agent thread ID
    return { ...createdGoal, agentThreadId: agentThread.id };
    
  } catch (agentThreadError) {
    console.error('Failed to create agent thread:', agentThreadError);
    // Clean up: Delete the goal since agent thread creation failed
    await supabase.from("planner_goals").delete().eq("id", createdGoal.id);
    throw new Error('Failed to create agent thread for goal');
  }
}

export async function updateGoal(goalId: string, updates: Partial<Goal>, userId: string): Promise<Goal> {
  // Build database update object directly from only the provided fields
  const dbUpdates: Partial<Omit<DbGoal, "created_at" | "updated_at" | "id" | "user_id">> = {};
  
  // Only include fields that are explicitly provided in updates
  if (updates.title !== undefined) dbUpdates.title = updates.title;
  if (updates.description !== undefined) dbUpdates.description = updates.description ?? null;
  if (updates.category !== undefined) dbUpdates.category = updates.category ?? null;
  if (updates.progress !== undefined) dbUpdates.progress = updates.progress;
  if (updates.dueDate !== undefined) dbUpdates.due_date = updates.dueDate ?? null;
  if (updates.status !== undefined) dbUpdates.status = updates.status ?? null;
  if (updates.isCompleted !== undefined) dbUpdates.is_completed = updates.isCompleted;
  if (updates.goalOrder !== undefined) dbUpdates.goal_order = updates.goalOrder ?? 0;
  if (updates.openaiThreadId !== undefined) dbUpdates.openai_thread_id = updates.openaiThreadId ?? null;

  const { data, error } = await supabase
    .from("planner_goals")
    .update(dbUpdates)
    .eq("id", goalId)
    .select()
    .single();
  if (error) throw error;
  return mapDbGoalToUiGoal(data as DbGoal);
}

export async function deleteGoal(goalId: string, userId: string) {
  const { error } = await supabase
    .from("planner_goals")
    .delete()
    .eq("id", goalId);
  if (error) throw error;
}

export async function reorderGoals(goalIdsOrdered: string[], userId: string) {
  // Bulk update each goal's goal_order based on order in the array
  const updates = goalIdsOrdered.map((goalId, idx) =>
    supabase.from("planner_goals").update({ goal_order: idx + 1 }).eq("id", goalId),
  );
  await Promise.all(updates);
}

/**
 * Automatically update goal completion status based on task completion
 * This should be called whenever tasks are completed/uncompleted for a goal
 */
export async function updateGoalCompletionStatus(goalId: string, userId: string): Promise<void> {
  try {
    // Get all tasks for this goal
    const { data: tasks, error: tasksError } = await supabase
      .from("planner_tasks")
      .select("id, is_completed")
      .eq("goal_id", goalId)
      .eq("user_id", userId);

    if (tasksError) {
      console.error('Error fetching tasks for goal completion check:', tasksError);
      return;
    }

    if (!tasks || tasks.length === 0) {
      // No tasks for this goal, mark as not completed
      await supabase
        .from("planner_goals")
        .update({ 
          is_completed: false,
          progress: 0
        })
        .eq("id", goalId);
      return;
    }

    // Calculate completion percentage
    const completedTasks = tasks.filter(task => task.is_completed);
    const completionPercentage = Math.round((completedTasks.length / tasks.length) * 100);
    const isCompleted = completionPercentage === 100;

    // Update goal completion status and progress
    const { error: updateError } = await supabase
      .from("planner_goals")
      .update({ 
        is_completed: isCompleted,
        progress: completionPercentage
      })
      .eq("id", goalId);

    if (updateError) {
      console.error('Error updating goal completion status:', updateError);
    } else {
      console.log(`Goal ${goalId} completion updated: ${completionPercentage}% (${isCompleted ? 'completed' : 'in progress'})`);
    }
  } catch (error) {
    console.error('Error in updateGoalCompletionStatus:', error);
  }
}
